import type { RoundScript } from "./scriptedRound";

/**
 * The scripted rounds the dev tools select by id (the hook's `loadFixture`, the dev menu's playlist, `?fixtures=`) and
 * the e2e suite plays. Cells: row 0 is 0–3, row 1 is 4–7, row 2 is 8–11, the GROW rows 12–19. Coin tokens are tenths of
 * a line's bet (`c10` = 1×; decision 56). Each script is written for every line and plays at any count (fewer lines can
 * only take its trigger away); it states what lands, the rules (`holdGame.ts`) play the rest, and `sim.test.ts` checks
 * every one.
 */
export interface ScriptedFixture {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly script: RoundScript;
}

export const FIXTURE_SCRIPTS: readonly ScriptedFixture[] = [
  // The gems first: played in order, they fill every pot a gem at a time — each potion a level up — to the third gems,
  // which fill all three and start the bonus with every booster; then each pot on its own. Their coins are on the middle
  // row, which every line count plays.
  {
    id: "gems_one",
    label: "A gem into each pot",
    group: "Gems",
    script: {
      board: [[".", "c2", ".", "."], ["c4", "c6", "c8", "."], [".", ".", ".", "c2"]],
      marks: [
        { cell: 4, pot: "life" },
        { cell: 5, pot: "multi" },
        { cell: 6, pot: "grow" },
      ],
    },
  },
  {
    id: "gems_two",
    label: "A second gem into each pot",
    group: "Gems",
    script: {
      board: [["c4", ".", ".", "."], [".", "c10", "c2", "c6"], [".", "MINI", ".", "."]],
      potsBefore: { life: 1, multi: 1, grow: 1 },
      marks: [
        { cell: 5, pot: "life" },
        { cell: 6, pot: "multi" },
        { cell: 7, pot: "grow" },
      ],
    },
  },
  {
    id: "gems_full",
    label: "The third gems: every pot full, the bonus with every booster",
    group: "Gems",
    script: {
      board: [[".", ".", "c4", "."], ["c6", ".", "c2", "c4"], [".", "c8", ".", "."]],
      potsBefore: { life: 2, multi: 2, grow: 2 },
      marks: [
        { cell: 4, pot: "life" },
        { cell: 6, pot: "multi" },
        { cell: 7, pot: "grow" },
      ],
      bonus: { lands: { 1: { 10: "c4" }, 2: { 14: "c8" } }, drops: { 1: [4, 6] } },
    },
  },
  // One pot at a time: a gem, a second, the third, which fills it alone and starts the bonus with its booster alone.
  {
    id: "life_one",
    label: "LIFE: a gem",
    group: "LIFE gems",
    script: { board: [["c2", ".", ".", "."], [".", "c4", ".", "."], [".", ".", "c6", "."]], marks: [{ cell: 5, pot: "life" }] },
  },
  {
    id: "life_two",
    label: "LIFE: a second gem",
    group: "LIFE gems",
    script: { board: [[".", ".", "c8", "."], ["c2", ".", ".", "."], [".", "MINI", ".", "."]], potsBefore: { life: 1 }, marks: [{ cell: 4, pot: "life" }] },
  },
  {
    id: "life_full",
    label: "LIFE: the third gem, the bonus with LIFE",
    group: "LIFE gems",
    script: {
      board: [[".", "c4", ".", "."], [".", ".", "c6", "."], ["c2", ".", ".", "."]],
      potsBefore: { life: 2 },
      marks: [{ cell: 6, pot: "life" }],
      bonus: { lands: { 2: { 3: "c10" } } },
    },
  },
  {
    id: "multi_one",
    label: "MULTI: a gem",
    group: "MULTI gems",
    script: { board: [[".", ".", ".", "c4"], ["c6", ".", ".", "."], [".", "c2", ".", "."]], marks: [{ cell: 4, pot: "multi" }] },
  },
  {
    id: "multi_two",
    label: "MULTI: a second gem",
    group: "MULTI gems",
    script: { board: [["c2", ".", ".", "."], [".", ".", "c10", "."], [".", ".", ".", "c4"]], potsBefore: { multi: 1 }, marks: [{ cell: 6, pot: "multi" }] },
  },
  {
    id: "multi_full",
    label: "MULTI: the third gem, the bonus with MULTI",
    group: "MULTI gems",
    script: {
      board: [[".", ".", "c2", "."], [".", "c8", ".", "."], ["c4", ".", ".", "."]],
      potsBefore: { multi: 2 },
      marks: [{ cell: 5, pot: "multi" }],
      bonus: { lands: { 1: { 11: "c6" } }, drops: { 1: [5, 2] } },
    },
  },
  {
    id: "grow_one",
    label: "GROW: a gem",
    group: "GROW gems",
    script: { board: [[".", "c6", ".", "."], [".", ".", ".", "c2"], ["c4", ".", ".", "."]], marks: [{ cell: 7, pot: "grow" }] },
  },
  {
    id: "grow_two",
    label: "GROW: a second gem",
    group: "GROW gems",
    script: { board: [[".", ".", ".", "c4"], [".", "c2", ".", "."], [".", ".", "c8", "."]], potsBefore: { grow: 1 }, marks: [{ cell: 5, pot: "grow" }] },
  },
  {
    id: "grow_full",
    label: "GROW: the third gem, the bonus with GROW",
    group: "GROW gems",
    script: {
      board: [["c4", ".", ".", "."], [".", ".", ".", "c6"], [".", ".", "c2", "."]],
      potsBefore: { grow: 2 },
      marks: [{ cell: 7, pot: "grow" }],
      bonus: { lands: { 1: { 13: "c10" }, 2: { 17: "c4" } } },
    },
  },
  {
    id: "base_blank",
    label: "Nothing lands",
    group: "Base",
    script: { board: [[".", ".", ".", "."], ["c4", ".", ".", "."], [".", ".", ".", "."]] },
  },
  {
    id: "base_coins",
    label: "Coins and a jackpot coin, no line",
    group: "Base",
    script: { board: [["c2", ".", "MINI", "."], [".", "c10", ".", "c4"], ["c6", ".", ".", "MYS"]] },
  },
  {
    // The coins' golden digits at their widest (a line's bet of 1.00: 100.00 to 1250.00, as the money formatter writes
    // them), and a mystery jackpot on the base board.
    id: "base_big_values",
    label: "Big coin values: the golden digits at their widest",
    group: "Base",
    script: { board: [["c1000", ".", "c5000", "."], [".", "c12500", ".", "c200"], [".", ".", ".", "MJP"]] },
  },
  {
    id: "base_gem",
    label: "A gem into the LIFE pot",
    group: "Base",
    script: { board: [[".", "c4", ".", "."], [".", ".", "c8", "."], [".", ".", ".", "."]], marks: [{ cell: 1, pot: "life" }] },
  },
  {
    id: "base_tease",
    label: "A line one short: the last reel teases",
    group: "Base",
    script: { board: [[".", ".", ".", "."], ["c4", "c2", "c6", "."], [".", "MINI", ".", "."]] },
  },
  {
    id: "bonus_line",
    label: "A covered line starts the bonus",
    group: "Bonus",
    script: {
      board: [["c4", "c2", "c10", "c6"], [".", ".", "MINI", "."], [".", ".", ".", "."]],
      bonus: { lands: { 1: { 9: "c4" }, 3: { 5: "c20" } } },
    },
  },
  {
    id: "bonus_collect",
    label: "A collect coin collects the board",
    group: "Bonus",
    script: {
      board: [[".", ".", ".", "."], ["c2", "c4", "c6", "c8"], [".", "MINI", ".", "."]],
      bonus: { lands: { 1: { 2: "COL" }, 2: { 11: "c10" } } },
    },
  },
  {
    // Two collectors: the second collects every coin in view then, the first collect's sum not among them.
    id: "bonus_collect_two",
    label: "Two collect coins: two collectors",
    group: "Bonus",
    script: {
      board: [[".", ".", ".", "."], ["c2", "c4", "c6", "c8"], [".", "MINI", ".", "."]],
      bonus: { lands: { 1: { 2: "COL" }, 2: { 11: "c10", 8: "COL" } } },
    },
  },
  {
    id: "bonus_life",
    label: "LIFE refills the empty meter",
    group: "Boosters",
    script: {
      board: [["c4", "c4", "c4", "c4"], [".", ".", ".", "."], [".", ".", ".", "."]],
      potsBefore: { life: 1 },
      bonus: { lands: { 4: { 6: "c10" } } },
    },
  },
  {
    id: "bonus_multi",
    label: "MULTI erupts: ×2, then ×3 on one cell",
    group: "Boosters",
    script: {
      board: [[".", ".", ".", "."], [".", ".", ".", "."], ["c6", "c2", "c4", "c8"]],
      potsBefore: { multi: 2 },
      bonus: { lands: { 1: { 0: "c10" } }, drops: { 1: [8, 0], 2: [8] } },
    },
  },
  {
    id: "bonus_grow_royal",
    label: "GROW fills all 20 cells: ROYAL",
    group: "Boosters",
    script: {
      board: [["c2", "c2", "c2", "c2"], ["c4", ".", "c4", "."], [".", "c6", ".", "c6"]],
      potsBefore: { grow: 1 },
      bonus: {
        lands: {
          1: { 5: "c2", 7: "c4", 8: "c2", 10: "c4" },
          2: { 12: "c6", 13: "c8", 14: "c6", 15: "c8" },
          3: { 16: "c10", 17: "MINI", 18: "c10", 19: "c20" },
        },
      },
    },
  },
  {
    id: "bonus_grand",
    label: "A full board: GRAND",
    group: "Bonus",
    script: {
      board: [["c4", "c4", "c4", "c4"], ["c2", ".", "c2", "."], [".", "c6", ".", "c6"]],
      bonus: { lands: { 1: { 5: "c2", 7: "c2" }, 2: { 8: "c8", 10: "MINOR" } } },
    },
  },
  {
    id: "bonus_mystery",
    label: "Mysteries revealed at the end",
    group: "Bonus",
    script: {
      board: [["MYS", "c4", "MYS", "c2"], [".", ".", ".", "."], [".", ".", ".", "."]],
      bonus: { lands: { 1: { 5: "MYS" } }, reveals: { 0: "c20", 2: "MINI", 5: "c40" } },
    },
  },
  // The mystery jackpot (the designer's reel, 2026-10-12): it reveals a jackpot coin only, its reel spinning to it at
  // the bonus's end; one per jackpot, from cells across the middle row, then with a mystery coin.
  {
    id: "mystery_jackpot_mini",
    label: "A mystery jackpot spins to MINI",
    group: "Mystery jackpot",
    script: {
      board: [[".", ".", ".", "."], ["c4", "MJP", "c6", "c2"], [".", ".", ".", "."]],
      bonus: { lands: { 1: { 2: "c8" } }, reveals: { 5: "MINI" } },
    },
  },
  {
    id: "mystery_jackpot_minor",
    label: "A mystery jackpot spins to MINOR",
    group: "Mystery jackpot",
    script: {
      board: [[".", ".", ".", "."], ["c2", "c6", "MJP", "c4"], [".", ".", ".", "."]],
      bonus: { lands: { 2: { 9: "c10" } }, reveals: { 6: "MINOR" } },
    },
  },
  {
    id: "mystery_jackpot_major",
    label: "A mystery jackpot spins to MAJOR",
    group: "Mystery jackpot",
    script: {
      board: [[".", ".", ".", "."], ["MJP", "c4", "c2", "c6"], [".", "c10", ".", "."]],
      bonus: { reveals: { 4: "MAJOR" } },
    },
  },
  {
    id: "mysteries_both",
    label: "A mystery coin and two mystery jackpots, revealed in turn",
    group: "Mystery jackpot",
    script: {
      board: [[".", ".", ".", "."], ["MYS", "c4", "MJP", "c2"], [".", ".", ".", "."]],
      bonus: { lands: { 1: { 9: "MJP" }, 2: { 0: "c6" } }, reveals: { 4: "c20", 6: "MINOR", 9: "MAJOR" } },
    },
  },
  {
    id: "pot_trigger",
    label: "The third gem fills GROW: the bonus starts",
    group: "Boosters",
    script: {
      board: [[".", "c4", ".", "."], [".", ".", "c8", "."], [".", ".", ".", "c2"]],
      potsBefore: { grow: 2, life: 1 },
      marks: [{ cell: 6, pot: "grow" }],
      bonus: { lands: { 1: { 13: "c4" }, 5: { 18: "MINOR" } } },
    },
  },
  {
    id: "pot_draw",
    label: "A full LIFE pot: its bonus starts at random, no gem this spin",
    group: "Boosters",
    script: {
      board: [[".", "c6", ".", "."], ["c2", ".", ".", "."], [".", ".", ".", "c4"]],
      potsBefore: { life: 3 },
      bonus: { lands: { 2: { 9: "c8" } } },
    },
  },
  {
    id: "big_win",
    label: "A big win: MAJOR ×2",
    group: "Bonus",
    script: {
      board: [["MAJOR", "c10", "c10", "c10"], [".", ".", ".", "."], [".", ".", ".", "."]],
      potsBefore: { multi: 1 },
      bonus: { lands: { 1: { 4: "c20" } }, drops: { 1: [0] } },
    },
  },
];
